import { API_ENDPOINTS } from '@/api/endpoints';
import { getSelectedOrganization } from '@/organization/orgUtils';
import { apiFetch } from '@/util/apiClient';
import { isAuthTimeoutError } from '@/util/errors';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

interface BudgetLine {
  lineType: string;
  category: string;
  amount: number;
  inCapEx: boolean;
  description: string;
}

export interface Project {
  id: string;
  name: string;
  description: string;
  projectLifetimeYears?: number;
  projectMajorMaintenanceYears?: number;
  address?: string;
  firstOperationYear?: string;
  firstConstructionYear?: string;
  firstFundingYear?: string;
  userProjectId?: string;
  status?: string;
  sponsoringDepts?: string[];
  programManagers?: string[];
  projectManagers?: string[];
  budgetOfficers?: string[];
  usingDepts?: string[];
  overseeingDepts?: string[];
  budget: Record<string, BudgetLine[]>;
  deletedAt?: string; // archived projects have a deletedAt field
  designApproved?: boolean;
  designApprovedAlternative?:
    | 'conventional'
    | 'reduced-carbon'
    | 'zero-carbon'
    | null;
  budgetApproved?: boolean;
  budgetApprovedAlternative?:
    | 'conventional'
    | 'reduced-carbon'
    | 'zero-carbon'
    | null;
  pencilsDown?: boolean; // project lock status
  pencilsDownDate?: string; // when pencils down was set
  currentWorkflow?: 'decarb' | 'resiliency' | 'details' | null; // tracks which workflow/hub this project is currently active in
  organization?: string; // Organization ID
  department?: string; // Optional department ID
  departmentDetails?: {
    id: string;
    name: string;
  };
  fiscalYear?: string; // Fiscal year ID
  fiscalYearDetails?: {
    id: string;
    name: string;
    startDate: string;
    endDate: string;
    isActive: boolean;
  };
}

// Data needed to create a new project
interface CreateProjectInput {
  name: string;
  description: string;
  projectLifetimeYears: number;
  projectMajorMaintenanceYears: number;
  organization?: string;
  department?: string;
  fiscalYear?: string;
}

interface ProjectStore {
  projects: Project[];
  new: (details: CreateProjectInput) => Promise<Project>;
  get: (projectId: string) => Project | undefined;
  fetch: (organizationId?: string) => Promise<Project[]>;
}

export const useProjectStore = create<ProjectStore>()(
  persist<ProjectStore>(
    (set, get) => ({
      projects: [],
      new: async (details) => {
        try {
          const orgId = details.organization;
          if (!orgId) {
            throw new Error('No organization specified');
          }

          const response = await apiFetch<Project>(
            API_ENDPOINTS.ORGANIZATIONS.PROJECTS.CREATE(orgId),
            {
              method: 'POST',
              body: JSON.stringify(details),
            },
          );

          await get().fetch(orgId);

          return response;
        } catch (error) {
          console.error(
            'Unable to create project',
            error instanceof Error ? error.message : 'Unknown error',
          );
          throw error;
        }
      },
      get: (projectId: string) => {
        const projects = get().projects.filter((p) => p.id === projectId);
        if (projects.length === 0) {
          return undefined;
        }
        return projects[0];
      },
      fetch: async (organizationId?: string) => {
        try {
          const selectedOrg = getSelectedOrganization();
          const orgId = organizationId ?? selectedOrg?.id;

          if (!orgId) {
            console.warn('No organization ID available for fetching projects');
            set({ projects: [] });
            return [];
          }

          // Use the organization-specific endpoint
          const projects = await apiFetch<Project[]>(
            API_ENDPOINTS.ORGANIZATIONS.PROJECTS.LIST(orgId),
          );

          set({ projects });
          return projects;
        } catch (error) {
          // Only log errors that aren't auth timeout related
          if (!isAuthTimeoutError(error)) {
            console.error(
              'Unable to get projects',
              error instanceof Error ? error.message : 'Unknown error',
            );
          }
          return [];
        }
      },
    }),
    {
      name: 'project-store',
      version: 1,
    },
  ),
);
